import { type TFunction } from 'i18next'
import {
  buildDiscountSteps,
  getActiveStep,
  isAllReached,
  type DiscountGoal,
  type DiscountStep,
  type NormalizedDiscountData,
} from './discountSteps'
import {
  getAllReachedText,
  getStepBadgeLabel,
  getStepNotMetText,
} from './discountStepsText'

export type DiscountStepViewProps = {
  goal: DiscountGoal
  applied: boolean
  progress: number
  badgeLabel: string
}

export type DiscountProgressProps = {
  // Ordered by unlock threshold.
  steps: DiscountStepViewProps[]
  // Progress toward the active goal; 0 when all reached.
  progress: number
  allReached: boolean
  // The active goal's copy, or the joined applied copy when all reached.
  text: string
}

// A lone buyover tier gets no chip; chips mark a ladder of tiers. While that
// hidden tier is the active goal, an unreached shipping chip would draw the fill
// toward itself, so it hides too.
function getChipSteps(
  steps: DiscountStep[],
  active: DiscountStep | undefined
): DiscountStep[] {
  const buyoverTiers = steps.filter(
    (step) => step.goal === 'buyover' || step.goal === 'percent'
  )
  if (buyoverTiers.length !== 1) return steps
  const [loneTier] = buyoverTiers
  return steps.filter(
    (step) =>
      step !== loneTier &&
      !(step.goal === 'shipping' && !step.applied && active === loneTier)
  )
}

export function buildDiscountProgressProps(
  data: NormalizedDiscountData,
  t: TFunction
): DiscountProgressProps | null {
  const steps = buildDiscountSteps(data)
  if (steps.length === 0) return null

  const active = getActiveStep(steps)
  const allReached = isAllReached(steps)

  const text = active
    ? getStepNotMetText(active, data.currency, t, { compact: true })
    : getAllReachedText(steps, data.currency, t)

  return {
    steps: getChipSteps(steps, active).map((step) => ({
      goal: step.goal,
      applied: step.applied,
      progress: step.progress,
      badgeLabel: getStepBadgeLabel(step, t),
    })),
    progress: active?.progress ?? 0,
    allReached,
    text,
  }
}
